<template>
  <ul v-if="langPanelOpen" class="list-none">
    <li>
      <BackButton class="px-8 font-semibold" @back="close" />
    </li>
    <li v-for="language in languages" :key="`lang-${language.isoCode}`">
      <a
        class="flex w-full border-b border-gray-200 hover:bg-white text-gray-600 text-16 font-bold tracking-wider py-6 px-8"
        :class="{ 'text-brand-800 underline': language.isoCode === $lang }"
        :href="language.url"
        @click.prevent="selectLanguage(language.isoCode, language.url)"
      >
        {{ language.label }}
      </a>
    </li>
  </ul>
  <button v-else class="flex items-center px-4 h-12 bg-white rounded-6 mx-8 mb-4" @click="open">
    <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-md"   alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
<path fill="#494949" d="m22.4 20.3-4.2-10.2a1 1 0 0 0-1.9 0l-4.2 10.2a1 1 0 0 0 1.3 1.4 1 1 0 0 0 .6-.6l.8-2h4.9l.8 2a1 1 0 0 0 2-.3 1 1 0 0 0 0-.5ZM15.7 17l1.6-3.8 1.5 3.8h-3.1Zm-3.1-1a1 1 0 0 0-.3-1.4s-.7-.5-1.7-1.6c1.9-2.5 3-5.4 3.4-6.7h1.5a1 1 0 0 0 0-2H10v-1a1 1 0 0 0-2 0v1H2.5a1 1 0 1 0 0 2h9.3c-.5 1.2-1.3 3.2-2.5 5-1.5-1.9-2-3.2-2-3.2a1 1 0 0 0-2 .8c0 .1.7 1.6 2.5 4l.1.1c-1.8 2.1-3.6 3.4-4.4 3.8a1 1 0 1 0 1 1.8c.1 0 2.3-1.2 4.8-4a14 14 0 0 0 1.8 1.7 1 1 0 0 0 1.5-.2Z"/>
</svg>
    <span class="truncate mx-2" aria-hidden="true">
      <span
        v-for="language in languages"
        :key="language.isoCode"
        class="dot uppercase mr-2 text-gray-300"
        :class="{ 'text-brand-800 font-semibold': language.isoCode === $lang }"
        >{{ language.isoCode }}</span
      >
    </span>
    <svg role="presentation" focusable="false" tabindex="-1" class="icon-sm shrink-0 text-brand-800 -rotate-90"
       
      alt="language selector"
      svg-inline
     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 19"><title>language selector</title>
  <path fill="currentColor" fill-rule="evenodd"
    d="M.8 1.18a2.26 2.26 0 0 1 3.2 0L16 13.2 28.03 1.18a2.26 2.26 0 0 1 3.2 3.2L17.6 17.97c-.88.9-2.31.9-3.2 0L.8 4.39a2.26 2.26 0 0 1 0-3.2Z"
    clip-rule="evenodd" />
</svg>
  </button>
</template>

<script lang="ts" setup>
import {
  HeaderLanguageItemClick,
  HeaderLanguageMenuOpen,
} from '@core/analytics/core-events/navigation'
import Cookie, { COOKIE_LANG } from '@gifsa/cookie-utils'
import type { AlternateLanguage } from './types'

const props = defineProps<{ alternateLanguages: AlternateLanguage[] }>()

const emit = defineEmits(['toggle'])

const { $lang, $track } = useNuxtApp()
const { name } = useRuntimeConfig().public

const langPanelOpen = ref(false)

const languages = computed(() => {
  const activeLanguages = getLanguageSelections(name)
  // Move current lang to the front
  const currentLangIndex = activeLanguages.findIndex((l) => l.isoCode === $lang)
  if (currentLangIndex !== -1) {
    const removed = activeLanguages.splice(currentLangIndex, 1)
    activeLanguages.unshift(...removed)
  }
  return activeLanguages.map((language) => {
    const pageLanguage = props.alternateLanguages.find((l) => l.language === language.isoCode)
    return {
      isoCode: language.isoCode,
      label: language.label,
      url: pageLanguage?.slug ?? getHomeUrl(language.isoCode),
    }
  })
})

function close() {
  emit('toggle', false)
  langPanelOpen.value = false
}

async function open() {
  emit('toggle', true)
  langPanelOpen.value = true
  await $track(HeaderLanguageMenuOpen())
}

async function selectLanguage(lang: string, url: string) {
  await $track(HeaderLanguageItemClick(lang))
  document.cookie = Cookie.generate(COOKIE_LANG, lang)
  window.location.href = url
}
</script>

<style scoped>
.dot::after {
  content: '\00B7';
  @apply ml-2 text-gray-300 font-bold;
}
</style>
